import { TrackingFrame } from "mannequin/src/types/tracking/mannequin";
import { RingBuffer } from "ring-buffer-ts";
import {
  Box3,
  DataTexture,
  LuminanceFormat,
  Material,
  Matrix4,
  Mesh,
  Object3D,
  PerspectiveCamera,
  Quaternion,
  RedFormat,
  Vector3,
  WebGLRenderer,
} from "three";

import { Axes, JointPositions } from "../types";
import { SkeletalPosition } from "../types/statsApi";

export const ftToMeter = 3.281;
export const dT = 1 / 30;

/**
 * Find index of target value in sortedArray
 * @param sortedArray RingBuffer "array"
 * @param getValue function to extract comparible value from array
 * @param target target value
 * @returns
 */
export function binarySearch<T>(
  sortedArray: RingBuffer<T>,
  getValue: (obj: T | undefined) => number,
  target: number
): number | null {
  let floor = 0;
  let ceil = sortedArray.getBufferLength() - 1;
  if (ceil <= 0) {
    return null;
  } else if (target < getValue(sortedArray.get(0))) {
    return 0;
  } else if (target > getValue(sortedArray.get(ceil))) {
    return ceil;
  }

  while (floor <= ceil) {
    const mid = floor + Math.floor((ceil - floor) / 2);
    const val = getValue(sortedArray.get(mid));

    if (val < target) {
      floor = mid + 1;
    } else if (val > target) {
      ceil = mid - 1;
    } else {
      return mid;
    }
  }

  const dFloor = getValue(sortedArray.get(floor)) - target;
  const dCeil = getValue(sortedArray.get(ceil)) - target;

  return dFloor < dCeil ? floor : ceil;
}

const m = new Matrix4();
export class Basis implements Axes {
  pos: Vector3;
  fwd = new Vector3(1, 0, 0);
  up = new Vector3(0, 1, 0);
  right = new Vector3(0, 0, 1);

  constructor(pos?: Vector3) {
    this.pos = pos ? pos : new Vector3();
  }

  reset() {
    this.fwd.set(1, 0, 0);
    this.up.set(0, 1, 0);
    this.right.set(0, 0, 1);
  }

  setFromQuaternion(q: Quaternion) {
    m.makeRotationFromQuaternion(q);
    m.extractBasis(this.fwd, this.right, this.up);
  }
}

export function matrix4Print(matrix: Matrix4 | undefined) {
  if (!matrix) {
    return;
  }
  let str = "[[";
  for (let i = 0; i < 4; i++) {
    if (i > 0) {
      str += " [";
    }
    for (let j = 0; j < 4; j++) {
      str += matrix.elements[i * 4 + j].toFixed(4);
      if (j < 3) {
        str += ", ";
      }
    }
    str += "]";
    if (i < 3) {
      str += "\n";
    }
  }
  str += "]";

  console.log(str);
}

const jointMetaData = {
  "0": "Nose",
  "1": "Neck",
  "2": "RShoulder",
  "3": "RElbow",
  "4": "RWrist",
  "5": "LShoulder",
  "6": "LElbow",
  "7": "LWrist",
  "8": "MidHip",
  "9": "RHip",
  "10": "RKnee",
  "11": "RAnkle",
  "12": "LHip",
  "13": "LKnee",
  "14": "LAnkle",
  "15": "REye",
  "16": "LEye",
  "17": "REar",
  "18": "LEar",
  "19": "LBigToe",
  "20": "LSmallToe",
  "21": "LHeel",
  "22": "RBigToe",
  "23": "RSmallToe",
  "24": "RHeel",
  "25": "LThumb",
  "26": "LPinky",
  "27": "RThumb",
  "28": "RPinky",
};

export class JointMap {
  jointNames: { [key: string]: string };
  pointFactories: ((points: JointPositions) => void)[];

  constructor(pointFactories: ((points: JointPositions) => void)[] = []) {
    this.jointNames = jointMetaData;
    this.pointFactories = pointFactories;
  }

  asJointPositions(pointData: SkeletalPosition): JointPositions {
    const points = new JointPositions();
    for (const jointData of pointData.joints) {
      if (this.jointNames.hasOwnProperty(jointData.id)) {
        const name = this.jointNames[jointData.id];
        // convert to GL coords here
        points.set(name, new Vector3(jointData.x, jointData.z, -jointData.y));
      } else {
        // console.warn(`no id ${jointData.id} in jointNames`);
      }
    }

    for (const factory of this.pointFactories) {
      factory(points);
    }

    return points;
  }
}

const _x = new Vector3();
const _y = new Vector3();
const _z = new Vector3();
export function lookAtRight(
  m: Matrix4,
  eye: Vector3,
  target: Vector3,
  up: Vector3
) {
  const te = m.elements;

  _z.subVectors(eye, target);

  if (_z.lengthSq() == 0) {
    // eye and target are in the same position
    _z.z = 1;
  }

  _z.normalize();
  _x.crossVectors(up, _z);

  if (_x.lengthSq() === 0) {
    // up and z are parallel
    if (Math.abs(up.z) === 1) {
      _z.x += 0.0001;
    } else {
      _z.z += 0.0001;
    }

    _z.normalize();
    _x.crossVectors(up, _z);
  }

  _x.normalize();
  _y.crossVectors(_z, _x);

  te[0] = _y.x;
  te[4] = _z.x;
  te[8] = _x.x;
  te[1] = _y.y;
  te[5] = _z.y;
  te[9] = _x.y;
  te[2] = _y.z;
  te[6] = _z.z;
  te[10] = _x.z;
}

export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(min, value), max);
}

export function quaternionAngle(q: Quaternion): number {
  return 2 * Math.acos(clampNumber(q.w, -1, 1));
}

export function quaternionAxis(q: Quaternion, a: Vector3) {
  const den = Math.sin(quaternionAngle(q) / 2);

  a.x = q.x / den;
  a.y = q.y / den;
  a.z = q.z / den;
}

const tempV = new Vector3();
export function quaternionSetAngle(q: Quaternion, angle: number) {
  quaternionAxis(q, tempV);
  const mul = Math.sin(angle / 2);

  q.set(tempV.x * mul, tempV.y * mul, tempV.z * mul, angleToW(angle));
}

export function quaternionAngleMultiply(q: Quaternion, scalar: number) {
  const angle = quaternionAngle(q);
  quaternionSetAngle(q, angle * scalar);
}

export function randomiseQuaternion(q: Quaternion) {
  q.set(
    0.5 - Math.random(),
    0.5 - Math.random(),
    0.5 - Math.random(),
    0.5 - Math.random()
  ).normalize();
}

const tempU = new Vector3();
const tempA = new Vector3();
const tempB = new Vector3();
const xAxis = new Vector3(1, 0, 0);
const yAxis = new Vector3(0, 1, 0);

export function quaternionBetweenVectors(
  q: Quaternion,
  a: Vector3,
  b: Vector3
) {
  tempA.copy(a).normalize();
  tempB.copy(b).normalize();

  const dot = tempA.dot(tempB);

  if (dot > 0.999999) {
    q.set(0, 0, 0, 1);
    return;
  } else if (dot < -0.999999) {
    tempU.crossVectors(xAxis, tempA);
    if (tempU.length() < 0.000001) {
      tempU.crossVectors(yAxis, tempA);
    }

    tempU.normalize();
    q.setFromAxisAngle(tempU, Math.PI);
  }

  tempU.crossVectors(tempA, tempB);
  q.x = tempU.x;
  q.y = tempU.y;
  q.z = tempU.z;
  q.w = 1 + dot;

  q.normalize();
}

export function quaternionsAlmostEqual(
  a: Quaternion,
  b: Quaternion,
  epsilon = 1e-7
) {
  const aArr = a.toArray();
  const bArr = b.toArray();
  const diff = aArr.map((v, i) => Math.abs(v - bArr[i]));

  for (const d of diff) {
    if (d > epsilon) {
      return false;
    }
  }

  return true;
}

export function quaternionNlerp(
  target: Quaternion,
  other: Quaternion,
  alpha: number
) {
  if (alpha <= 0) {
    return;
  }
  if (alpha > 1) {
    alpha = 1;
  }

  target.x = target.x * (1 - alpha) + other.x * alpha;
  target.y = target.y * (1 - alpha) + other.y * alpha;
  target.z = target.z * (1 - alpha) + other.z * alpha;
  target.w = target.w * (1 - alpha) + other.w * alpha;

  target.normalize();
}

export function angleToW(a: number): number {
  return Math.cos(a / 2);
}

export function findCentroid(
  points: Vector3[],
  centroid: Vector3,
  len?: number
) {
  centroid.set(0, 0, 0);
  if (!len) {
    len = points.length;
  }

  for (let i = 0; i < len; i++) {
    centroid.add(points[i]);
  }

  centroid.divideScalar(len);
}

export function fetchExtractJson(response: Response) {
  if (response.ok) {
    return response.json();
  } else {
    throw new Error(`unable to load ${response.url} - ${response.status}`);
  }
}

export function vectorIsNaN(v: Vector3): boolean {
  return isNaN(v.x) || isNaN(v.y) || isNaN(v.z);
}

export function alphaFilter(prevEst: number, value: number, alpha: number) {
  return prevEst + alpha * (value - prevEst);
}

export function alphaFilterVector3(
  runningEst: Vector3,
  value: Vector3,
  alpha: number
) {
  runningEst.set(
    alphaFilter(runningEst.x, value.x, alpha),
    alphaFilter(runningEst.y, value.y, alpha),
    alphaFilter(runningEst.z, value.z, alpha)
  );
}

export function nearestFrameIndex(
  data: RingBuffer<TrackingFrame>,
  timestamp: number
): number | undefined {
  // Assumes data is sorted by time
  const index = binarySearch(
    data,
    (frame) => (frame !== undefined ? frame.time : 0),
    timestamp
  );

  return index != null ? index : undefined;
}

export function disableContextMenu(event: any) {
  event.preventDefault();
  event.stopPropagation();
}

export function boxDims(box: Box3) {
  return {
    x: box.max.x - box.min.x,
    y: box.max.y - box.min.y,
    z: box.max.z - box.min.z,
  };
}

const camDir = new Vector3();
export function cameraAzimuthElevation(
  camera: PerspectiveCamera
): [number, number] {
  camera.getWorldDirection(camDir);
  return [
    -Math.atan2(camDir.x, -camDir.z),
    -Math.asin(
      camDir.y /
        Math.sqrt(
          camDir.x * camDir.x + camDir.y * camDir.y + camDir.z * camDir.z
        )
    ),
  ];
}

export function toEpoch(iso8601ts: string) {
  return new Date(iso8601ts).valueOf();
}

export function saveToFile(json: string, filename: string) {
  const a = document.createElement("a");
  const file = new Blob([json], { type: "text/plain" });

  a.href = URL.createObjectURL(file);
  a.download = filename;
  a.click();
}

export function approxRollingAverage(
  average: number,
  value: number,
  history = 50
) {
  average -= average / history;
  average += value / history;

  return average;
}

export function disposeMeshGeometry(c: Object3D) {
  const mesh = c as Mesh;
  if (mesh.isMesh) {
    mesh.geometry.dispose();
  }
}

export function disposeMats(mat: Material) {
  for (const key of Object.keys(mat)) {
    const value = mat[key];
    if (!value) continue;

    if (value.dispose && typeof value.dispose === "function") {
      value.dispose();
    }

    const mapTypes = [
      "map",
      "normalMap",
      "aoMap",
      "metalnessMap",
      "roughnessMap",
    ];
    for (const mapType of mapTypes) {
      if (value[mapType]?.dispose && value[mapType].dispose === "function") {
        value[mapType].dispose();
      }
    }
  }

  mat.dispose();
}

export function disposeObjects(obj: any) {
  if (obj.material) {
    if (obj.material instanceof Array) {
      for (const mat of obj.material) {
        disposeMats(mat);
      }
    } else {
      disposeMats(obj.material);
    }
  }
  if (obj.geometry) {
    obj.geometry?.dispose();
  }
}

export function getRandomElement<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}

export function arraysEqual<T>(a: Array<T>, b: Array<T>): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

export function almostEqual(a: number, b: number, epsilon = 0.001): boolean {
  return Math.abs(a - b) < epsilon;
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function ensureFlagIsSet(object: any, name: string) {
  return new Promise<void>((resolve) => {
    (function waitForFoo() {
      if (object[name]) return resolve();
      setTimeout(waitForFoo, 30);
    })();
  });
}

// from https://stackoverflow.com/questions/25582882/javascript-math-random-normal-distribution-gaussian-bell-curve
export function randomNormal(): number {
  let u = 0,
    v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  let num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  num = num / 10.0 + 0.5;
  if (num > 1 || num < 0) return randomNormal();
  return num;
}

export function makeGradientMap(
  size: number,
  renderer: WebGLRenderer,
  floor = 0,
  ceiling = 256
): DataTexture {
  const format = renderer.capabilities.isWebGL2 ? RedFormat : LuminanceFormat;

  if (floor < 0 || floor > ceiling || ceiling > 256) {
    console.warn("gradient map limits invalid");
  }

  const spread = ceiling - floor;

  const colors = new Uint8Array(size);
  for (let c = 0; c <= colors.length; c++) {
    colors[c] = floor + (c / colors.length) * spread;
  }

  const map = new DataTexture(colors, colors.length, 1, format);
  map.needsUpdate = true;

  return map;
}

// from https://stackoverflow.com/a/61132308
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};
